import React, { useState, useEffect } from "react";
import html2canvas from "html2canvas";
import { collection, addDoc, getDocs, query, orderBy, where, deleteDoc, doc, updateDoc } from "firebase/firestore";
import { db } from "../firebase"; 
import ReceiptMobile from "../components/ReceiptMobile";

const VERIFY_HOST = "https://verify.cavelen.com";

const generateHexToken = () => {
  try {
    const arr = new Uint8Array(12);
    window.crypto.getRandomValues(arr);
    return Array.from(arr).map(b => b.toString(16).padStart(2, "0")).join("") + Date.now().toString(36);
  } catch (e) {
    return Math.random().toString(36).slice(2) + Date.now().toString(36);
  }
};

const generateTransactionId = () => {
  const token = generateHexToken().toUpperCase().replace(/[^A-Z0-9]/g, "");
  return `TXN-${token.slice(0, 4)}-${token.slice(4, 8)}-${token.slice(8, 12)}-${token.slice(12, 16)}`;
};

const buildReferenceId = (date, id) => {
  const compactDate = String(date || new Date().toISOString().slice(0, 10)).replaceAll("-", "");
  const suffix = String(id || generateHexToken()).replace(/[^a-zA-Z0-9]/g, "").slice(-6).toUpperCase();
  return `CVX-${compactDate}-${suffix}`;
};

const formatMoney = (symbol, value) => `${symbol}${(Number(value) || 0).toFixed(2)}`;

const formatDisplayDate = (dateValue) => {
  const parsed = dateValue ? new Date(`${dateValue}T00:00:00`) : new Date();
  if (Number.isNaN(parsed.getTime())) return dateValue || "";
  return parsed.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

const formatDisplayTime = (timestamp) => {
  const rawDate = timestamp?.toDate ? timestamp.toDate() : timestamp;
  const parsed = rawDate ? new Date(rawDate) : new Date();
  return parsed.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
};

const getVerifyUrl = (transactionId) => `${VERIFY_HOST}/?txn=${encodeURIComponent(transactionId || "")}`;

export default function RmbCalculationPage() {
  const today = new Date().toISOString().split('T')[0];

  const [pin, setPin] = useState("");
  const [isLocked, setIsLocked] = useState(true);

  const handleVerifyPin = (e) => {
    e.preventDefault();
    if (pin === "157500") setIsLocked(false);
    else { alert("⚠️ Wrong PIN! Try again."); setPin(""); }
  };

  const [sellData, setSellData] = useState({
    date: today, customerName: "", receivedMethod: "Cavelen", receivedAmount: "", sellingRate: "", purchaseRate: ""
  });

  const [buyData, setBuyData] = useState({
    date: today, customerName: "", receiveMethod: "Alipay", clearanceMethod: "Bank", rmbAmount: "", buyingRate: "", sellingRate: "" 
  });

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [receiptModal, setReceiptModal] = useState(null); 
  const [isReceiptExporting, setIsReceiptExporting] = useState(false);
  const [filterMonth, setFilterMonth] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const monthOptions = React.useMemo(() => {
    const options = [{ value: "All", label: "Last 30 Days" }];
    const seen = new Set();
    const base = new Date();
    for (let i = 0; i < 6; i++) {
      const d = new Date(base.getFullYear(), base.getMonth() - i, 1);
      const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (seen.has(value)) continue;
      seen.add(value);
      options.push({ value, label: d.toLocaleString('en-US', { month: 'short', year: 'numeric' }) });
    }
    return options;
  }, []);

  useEffect(() => {
    if (!isLocked) {
      const fetchForRange = async () => {
        try {
          let q;
          if (filterMonth === "All") {
            const thirtyDaysAgo = new Date(); thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
            q = query(collection(db, "rmb_transactions"), where("timestamp", ">=", thirtyDaysAgo), orderBy("timestamp", "desc"));
          } else {
            const [y, m] = filterMonth.split("-").map(Number);
            const start = new Date(y, m - 1, 1);
            const end = new Date(y, m, 1);
            q = query(
              collection(db, "rmb_transactions"),
              where("timestamp", ">=", start),
              where("timestamp", "<", end),
              orderBy("timestamp", "desc")
            );
          }

          const querySnapshot = await getDocs(q);
          const txns = [];
          querySnapshot.forEach((doc) => txns.push({ id: doc.id, ...doc.data() }));
          setTransactions(txns);
        } catch (error) { console.error("Error fetching data: ", error); }
      };
      fetchForRange();
    }
  }, [isLocked, filterMonth]);

  const calculateSellStats = () => {
    const amount = parseFloat(sellData.receivedAmount) || 0;
    const sellRate = parseFloat(sellData.sellingRate) || 0;
    const buyRate = parseFloat(sellData.purchaseRate) || 0;
    
    const chargePercentage = !["Cavelen", "IBBL", "Bank"].includes(sellData.receivedMethod) ? 1.5 : 0;
    const chargeAmount = amount * (chargePercentage / 100);
    const netAmount = amount - chargeAmount;
    const rmbToPay = sellRate > 0 ? (netAmount / sellRate) : 0;
    
    let profit = (buyRate > 0 && rmbToPay > 0) ? (rmbToPay * (sellRate - buyRate)) : 0;
    if (["bKash", "Nagad", "Rocket"].includes(sellData.receivedMethod)) profit += amount * 0.005; 

    return { amount, chargeAmount, netAmount, rmbToPay, sellRate, profit };
  };
  const sellStats = calculateSellStats();

  const calculateBuyStats = () => {
    const rmb = parseFloat(buyData.rmbAmount) || 0;
    const inputBuyRate = parseFloat(buyData.buyingRate) || 0;
    const sellRate = parseFloat(buyData.sellingRate) || 0; 
    
    const totalBdtToPay = rmb * inputBuyRate;
    
    let profit = (rmb > 0 && sellRate > 0) ? (rmb * (sellRate - inputBuyRate)) : 0;
    if (["bKash", "Nagad", "Rocket"].includes(buyData.clearanceMethod)) profit += totalBdtToPay * 0.015;

    return { rmb, rate: inputBuyRate ? inputBuyRate.toFixed(2) : 0, sellRate, totalBdtToPay, profit };
  };
  const buyStats = calculateBuyStats();

  // Aggregations computed from the currently loaded/filtered transactions

  const handleRecordSell = async () => {
    if (!sellData.customerName) return alert("Customer Name needed!");
    setLoading(true);
    const verifyToken = generateHexToken();
    const transactionId = generateTransactionId();

    const newTxn = {
      type: "Sell", date: sellData.date, customerName: sellData.customerName,
      rmb: parseFloat(sellStats.rmbToPay.toFixed(2)), bdt: parseFloat(sellStats.amount.toFixed(2)), rate: sellStats.sellRate,
      profit: parseFloat(sellStats.profit.toFixed(2)), timestamp: new Date(),
      receivedMethod: sellData.receivedMethod, chargeAmount: sellStats.chargeAmount, netAmount: sellStats.netAmount
      , verifyToken, transactionId
    };
    try {
      const docRef = await addDoc(collection(db, "rmb_transactions"), newTxn);
      const referenceId = buildReferenceId(sellData.date, docRef.id);
      await updateDoc(doc(db, "rmb_transactions", docRef.id), { referenceId });
      const savedTxn = { id: docRef.id, ...newTxn, referenceId };
      setTransactions([savedTxn, ...transactions]);
      setReceiptModal({ type: "sell", data: sellData, stats: sellStats, id: docRef.id, referenceId, transactionId, verifyToken, timestamp: newTxn.timestamp });
      setSellData({ ...sellData, customerName: "", receivedAmount: "" });
      setCurrentPage(1); 
    } catch (error) { alert("Error saving transaction!"); } finally { setLoading(false); }
  };

  const handleRecordBuy = async () => {
    if (!buyData.customerName) return alert("Customer Name needed!");
    setLoading(true);
    const verifyToken = generateHexToken();
    const transactionId = generateTransactionId();

    const newTxn = {
      type: "Buy", date: buyData.date, customerName: buyData.customerName,
      rmb: parseFloat(buyStats.rmb.toFixed(2)), bdt: parseFloat(buyStats.totalBdtToPay.toFixed(2)), rate: buyStats.rate,
      profit: parseFloat(buyStats.profit.toFixed(2)), timestamp: new Date(),
      receiveMethod: buyData.receiveMethod, clearanceMethod: buyData.clearanceMethod
      , verifyToken, transactionId
    };
    try {
      const docRef = await addDoc(collection(db, "rmb_transactions"), newTxn);
      const referenceId = buildReferenceId(buyData.date, docRef.id);
      await updateDoc(doc(db, "rmb_transactions", docRef.id), { referenceId });
      const savedTxn = { id: docRef.id, ...newTxn, referenceId };
      setTransactions([savedTxn, ...transactions]);
      setReceiptModal({ type: "buy", data: buyData, stats: buyStats, id: docRef.id, referenceId, transactionId, verifyToken, timestamp: newTxn.timestamp });
      setBuyData({ ...buyData, customerName: "", rmbAmount: "", buyingRate: "" });
      setCurrentPage(1); 
    } catch (error) { alert("Error saving transaction!"); } finally { setLoading(false); }
  };

  const handleDeleteTxn = async (id) => {
    if(window.confirm("Are you sure you want to permanently delete this transaction?")) {
      try {
        await deleteDoc(doc(db, "rmb_transactions", id));
        setTransactions(transactions.filter(t => t.id !== id));
      } catch(e) { alert("Delete failed: " + e.message); }
    }
  };

  const handleViewVoucher = (txn) => {
    // Ensure legacy transactions have persisted IDs before creating a verifiable slip.
    const ensureTokenAndOpen = async () => {
      let token = txn.verifyToken;
      let transactionId = txn.transactionId;
      let referenceId = txn.referenceId;
      const updates = {};

      if (!token) {
        token = generateHexToken();
        updates.verifyToken = token;
      }
      if (!transactionId) {
        transactionId = generateTransactionId();
        updates.transactionId = transactionId;
      }
      if (!referenceId) {
        referenceId = buildReferenceId(txn.date, txn.id);
        updates.referenceId = referenceId;
      }

      if (Object.keys(updates).length) {
        try {
          await updateDoc(doc(db, "rmb_transactions", txn.id), updates);
          setTransactions(transactions.map(item => item.id === txn.id ? { ...item, ...updates } : item));
        } catch (e) {
          console.warn("Could not persist receipt identifiers", e);
          alert("Could not create a verifiable transaction ID for this receipt. Please try again.");
          return;
        }
      }

      setReceiptModal({
        id: txn.id,
        referenceId,
        transactionId,
        verifyToken: token,
        type: txn.type.toLowerCase(),
        timestamp: txn.timestamp,
        data: {
          date: txn.date,
          customerName: txn.customerName,
          receivedMethod: txn.receivedMethod || "",
          receiveMethod: txn.receiveMethod || "",
          clearanceMethod: txn.clearanceMethod || ""
        },
        stats: {
          amount: parseFloat(txn.bdt) + (txn.chargeAmount || 0),
          chargeAmount: txn.chargeAmount || 0,
          netAmount: txn.netAmount || parseFloat(txn.bdt),
          sellRate: txn.rate,
          rmbToPay: parseFloat(txn.rmb), rmb: parseFloat(txn.rmb), rate: txn.rate, totalBdtToPay: parseFloat(txn.bdt)
        }
      });
    };
    ensureTokenAndOpen();
  };

  const filteredTransactions = React.useMemo(() => {
    if (filterMonth === "All") return transactions; // we already fetched last 30 days
    return transactions.filter(txn => typeof txn.date === 'string' && txn.date.startsWith(filterMonth));
  }, [transactions, filterMonth]);
  
  const totalFilteredProfit = React.useMemo(() => {
    return filteredTransactions.reduce((sum, txn) => sum + (parseFloat(txn.profit) || 0), 0);
  }, [filteredTransactions]);

  const last7FilteredProfit = React.useMemo(() => {
    const sevenDaysAgo = new Date(); sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    return filteredTransactions.filter(txn => new Date(txn.date) >= sevenDaysAgo).reduce((sum, txn) => sum + (parseFloat(txn.profit) || 0), 0);
  }, [filteredTransactions]);

  const todayFilteredProfit = React.useMemo(() => {
    return filteredTransactions.filter(txn => txn.date === today).reduce((sum, txn) => sum + (parseFloat(txn.profit) || 0), 0);
  }, [filteredTransactions, today]);
  const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);
  const currentTransactions = filteredTransactions.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const buildReceiptProps = (receipt) => {
    const isSell = receipt.type === "sell";
    const stats = receipt.stats || {};
    const data = receipt.data || {};
    const paidValue = isSell ? (stats.amount || stats.netAmount || 0) : (stats.rmb || 0);
    const receivedValue = isSell ? (stats.rmbToPay || 0) : (stats.totalBdtToPay || 0);
    const transactionId = receipt.transactionId || receipt.verifyToken || receipt.id;
    const method = isSell
      ? data.receivedMethod
      : [data.receiveMethod, data.clearanceMethod].filter(Boolean).join(" + ");
    const rate = stats.sellRate || stats.rate || 0;

    return {
      brand: "Cavelen XDL",
      paidAmount: formatMoney(isSell ? "৳" : "¥", paidValue),
      paidCurrency: isSell ? "BDT" : "CNY",
      receivedAmount: formatMoney(isSell ? "¥" : "৳", receivedValue),
      receivedCurrency: isSell ? "CNY" : "BDT",
      referenceId: receipt.referenceId || buildReferenceId(data.date, receipt.id),
      transactionId,
      date: formatDisplayDate(data.date),
      time: formatDisplayTime(receipt.timestamp),
      customerName: data.customerName || "",
      paymentMethod: method || "",
      exchangeRate: rate ? `1 CNY = ৳ ${Number(rate).toFixed(4)}` : "",
      serviceFee: formatMoney("৳", isSell ? (stats.chargeAmount || 0) : 0),
      totalPaid: formatMoney(isSell ? "৳" : "¥", paidValue),
      verifyUrl: getVerifyUrl(transactionId)
    };
  };

  const createReceiptImage = async () => {
    const receiptElement = document.getElementById("receipt-print-area");
    if (!receiptElement) throw new Error("Receipt is not available.");

    const canvas = await html2canvas(receiptElement, {
      backgroundColor: "#edf5ff",
      scale: 2,
      useCORS: true,
      logging: false,
      windowWidth: receiptElement.scrollWidth
    });

    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error("Could not create receipt image.")), "image/jpeg", 0.95);
    });
  };

  const downloadReceiptImage = (blob) => {
    const transactionId = receiptModal?.transactionId || "cavelen-receipt";
    const imageUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = imageUrl;
    link.download = `${transactionId}.jpg`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(imageUrl), 1000);
  };

  const handleDownloadReceipt = async () => {
    setIsReceiptExporting(true);
    try {
      downloadReceiptImage(await createReceiptImage());
    } catch (error) {
      console.error("Receipt download failed", error);
      alert("Could not create the receipt image. Please try again.");
    } finally {
      setIsReceiptExporting(false);
    }
  };

  const handleShareReceipt = async () => {
    setIsReceiptExporting(true);
    try {
      const blob = await createReceiptImage();
      const transactionId = receiptModal?.transactionId || "cavelen-receipt";
      const imageFile = new File([blob], `${transactionId}.jpg`, { type: "image/jpeg" });

      if (navigator.canShare?.({ files: [imageFile] })) {
        await navigator.share({ files: [imageFile], title: "Cavelen transaction receipt" });
        return;
      }

      downloadReceiptImage(blob);
      alert("Your browser does not support image sharing. The JPG has been downloaded instead.");
    } catch (error) {
      if (error?.name !== "AbortError") {
        console.error("Receipt share failed", error);
        alert("Could not share the receipt image. Please try again.");
      }
    } finally {
      setIsReceiptExporting(false);
    }
  };

  const inputStyle = { width: "100%", padding: "10px", borderRadius: "8px", border: "1px solid #cbd5e1", fontSize: "14px", boxSizing: "border-box", background: "#f8fafc" };
  const labelStyle = { display: "block", fontSize: "11px", fontWeight: "700", color: "#64748b", margin: "12px 0 4px 0", textTransform: "uppercase", letterSpacing: "0.5px" };
  const cardStyle = { background: "#fff", padding: "24px", borderRadius: "16px", border: "1px solid #e2e8f0", boxShadow: "0 1px 3px 0 rgba(0,0,0,0.05)" };

  if (isLocked) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "70vh", width: "100%", padding: "20px" }}>
        <form onSubmit={handleVerifyPin} style={{ background: "#fff", padding: "32px", borderRadius: "20px", boxShadow: "0 10px 25px rgba(0,0,0,0.05)", border: "1px solid #e2e8f0", textAlign: "center", maxWidth: "340px", width: "100%" }}>
          <div style={{ width: "56px", height: "56px", borderRadius: "50%", background: "linear-gradient(135deg, #0B47B3, #2563eb)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px auto" }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
          </div>
          <h3 style={{ margin: "0 0 8px 0", fontSize: "18px", fontWeight: "700", color: "#1e293b" }}>Workspace Locked</h3>
          <p style={{ fontSize: "13px", color: "#64748b", marginBottom: "20px" }}>Enter PIN to access trading desk.</p>
          <input type="password" placeholder="••••••" maxLength={6} style={{ ...inputStyle, textAlign: "center", fontSize: "20px", letterSpacing: "6px", marginBottom: "16px", background: "#fff" }} value={pin} onChange={(e) => setPin(e.target.value)} />
          <button type="submit" style={{ width: "100%", padding: "12px", background: "#0B47B3", color: "white", border: "none", borderRadius: "10px", fontWeight: "600", cursor: "pointer" }}>Unlock</button>
        </form>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: "1200px", margin: "0 auto", paddingBottom: "40px" }}>
      <div style={{ marginBottom: "24px", padding: "0 10px", display: "flex", alignItems: "center", gap: 16, justifyContent: "space-between" }}>
        <h2 style={{ fontSize: "24px", fontWeight: "800", color: "#0f172a", margin: 0 }}>RMB Trading Desk</h2>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 340px), 1fr))", gap: "24px", padding: "0 10px" }}>
        
        {/* SELL COMPONENT */}
        <div style={cardStyle}>
          <h3 style={{ margin: "0 0 16px 0", color: "#ef4444", display: "flex", alignItems: "center", gap: "8px", fontSize: "16px", fontWeight: "700" }}><div style={{ width: 10, height: 10, borderRadius: "50%", background: "#ef4444" }}></div> Sell RMB</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
            <div><label style={labelStyle}>Date</label><input type="date" style={inputStyle} value={sellData.date} onChange={e => setSellData({...sellData, date: e.target.value})} /></div>
            <div><label style={labelStyle}>Customer Name</label><input type="text" placeholder="Name..." style={inputStyle} value={sellData.customerName} onChange={e => setSellData({...sellData, customerName: e.target.value})} /></div>
          </div>
          <label style={labelStyle}>Received BDT Method</label>
          <select style={inputStyle} value={sellData.receivedMethod} onChange={e => setSellData({...sellData, receivedMethod: e.target.value})}>
            <option value="Cavelen">Cavelen</option><option value="IBBL">IBBL</option><option value="Bank">Bank</option><option value="bKash">bKash</option><option value="Nagad">Nagad</option><option value="Rocket">Rocket</option>
          </select>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px" }}>
            <div><label style={labelStyle}>Rcvd ৳</label><input type="number" placeholder="BDT" style={inputStyle} value={sellData.receivedAmount} onChange={e => setSellData({...sellData, receivedAmount: e.target.value})} /></div>
            <div><label style={labelStyle}>Sell Rate</label><input type="number" step="0.01" placeholder="Rate" style={inputStyle} value={sellData.sellingRate} onChange={e => setSellData({...sellData, sellingRate: e.target.value})} /></div>
            <div><label style={labelStyle}>Buy Rate</label><input type="number" step="0.01" placeholder="Cost" style={inputStyle} value={sellData.purchaseRate} onChange={e => setSellData({...sellData, purchaseRate: e.target.value})} /></div>
          </div>

          <div style={{ marginTop: "16px", padding: "12px", background: "#fef2f2", borderRadius: "10px", color: "#991b1b", fontSize: "14px" }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Charge {!["Cavelen", "IBBL", "Bank"].includes(sellData.receivedMethod) ? `(${sellData.receivedMethod} 1.5%)` : ""}:</span> 
              <span>- ৳ {sellStats.chargeAmount.toFixed(2)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontWeight: "bold", marginTop: "8px" }}><span>Total RMB To Pay:</span> <span>¥ {sellStats.rmbToPay.toFixed(2)}</span></div>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: "8px", fontSize: "12px" }}><span>Expected Profit:</span> <span style={{ color: sellStats.profit >= 0 ? "#16a34a" : "#ef4444", fontWeight: "600" }}>{sellStats.profit > 0 ? '+ ' : (sellStats.profit < 0 ? '- ' : '')} ৳ {Math.abs(sellStats.profit).toFixed(2)}</span></div>
          </div>

          <button onClick={handleRecordSell} disabled={loading} style={{ marginTop: "16px", width: "100%", padding: "14px", background: "#ef4444", color: "white", border: "none", borderRadius: "10px", fontWeight: "bold", cursor: "pointer", opacity: loading ? 0.7 : 1 }}>
            {loading ? "Processing..." : "Record Sale"}
          </button>
        </div>

        {/* BUY COMPONENT */}
        <div style={cardStyle}>
          <h3 style={{ margin: "0 0 16px 0", color: "#22c55e", display: "flex", alignItems: "center", gap: "8px", fontSize: "16px", fontWeight: "700" }}><div style={{ width: 10, height: 10, borderRadius: "50%", background: "#22c55e" }}></div> Buy RMB</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
            <div><label style={labelStyle}>Date</label><input type="date" style={inputStyle} value={buyData.date} onChange={e => setBuyData({...buyData, date: e.target.value})} /></div>
            <div><label style={labelStyle}>Customer Name</label><input type="text" placeholder="Name..." style={inputStyle} value={buyData.customerName} onChange={e => setBuyData({...buyData, customerName: e.target.value})} /></div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
            <div><label style={labelStyle}>Receive Method (RMB)</label><select style={inputStyle} value={buyData.receiveMethod} onChange={e => setBuyData({...buyData, receiveMethod: e.target.value})}><option value="Alipay">Alipay</option><option value="WeChat">WeChat</option><option value="Unpaid">Unpaid</option></select></div>
            <div><label style={labelStyle}>Clearance Method (BDT)</label><select style={inputStyle} value={buyData.clearanceMethod} onChange={e => setBuyData({...buyData, clearanceMethod: e.target.value})}><option value="Bank">Bank</option><option value="bKash">bKash</option><option value="Nagad">Nagad</option><option value="Rocket">Rocket</option></select></div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px" }}>
            <div><label style={labelStyle}>RMB Amt</label><input type="number" placeholder="¥ Amt" style={inputStyle} value={buyData.rmbAmount} onChange={e => setBuyData({...buyData, rmbAmount: e.target.value})} /></div>
            <div><label style={labelStyle}>Buy Rate</label><input type="number" step="0.01" placeholder="Rate" style={inputStyle} value={buyData.buyingRate} onChange={e => setBuyData({...buyData, buyingRate: e.target.value})} /></div>
            <div><label style={labelStyle}>Expected Sell Rate</label><input type="number" step="0.01" placeholder="Exp Rate" style={inputStyle} value={buyData.sellingRate} onChange={e => setBuyData({...buyData, sellingRate: e.target.value})} /></div>
          </div>

          <div style={{ marginTop: "16px", padding: "12px", background: "#f0fdf4", borderRadius: "10px", color: "#166534", fontSize: "14px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontWeight: "bold" }}><span>Total BDT To Pay:</span> <span>৳ {buyStats.totalBdtToPay.toFixed(2)}</span></div>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: "8px", fontSize: "12px" }}>
               <span>Calculated Rate: <strong style={{color: "#0f172a"}}>{buyStats.rate}</strong></span>
               <span>Expected Profit: <span style={{ color: buyStats.profit >= 0 ? "#16a34a" : "#ef4444", fontWeight: "600" }}>{buyStats.profit > 0 ? '+ ' : (buyStats.profit < 0 ? '- ' : '')} ৳ {Math.abs(buyStats.profit).toFixed(2)}</span></span>
            </div>
          </div>
          <button onClick={handleRecordBuy} disabled={loading} style={{ marginTop: "16px", width: "100%", padding: "14px", background: "#22c55e", color: "white", border: "none", borderRadius: "10px", fontWeight: "bold", cursor: "pointer", opacity: loading ? 0.7 : 1 }}>
            {loading ? "Processing..." : "Record Purchase"}
          </button>
        </div>
      </div>

      {/* RECENT TRANSACTIONS TABLE */}
      <div style={{ padding: "0 10px" }}>
        <div style={{ ...cardStyle, marginTop: "24px", overflow: "hidden", display: "flex", flexDirection: "column" }}>
          
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "16px", marginBottom: "20px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
              <h3 style={{ margin: 0, color: "#0f172a", fontSize: "18px", fontWeight: "700" }}>Transactions</h3>
              <select value={filterMonth} onChange={e => { setFilterMonth(e.target.value); setCurrentPage(1); }} style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid #cbd5e1", outline: "none", fontSize: 13, fontWeight: 600, color: "#475569" }}>
                {monthOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            </div>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <div style={{ background: "linear-gradient(135deg, #0B47B3, #2563eb)", color: "white", padding: "8px 16px", borderRadius: "20px", fontWeight: "600", fontSize: "13px", boxShadow: "0 4px 6px -1px rgba(59, 130, 246, 0.2)" }}>
                Today's Profit: ৳ {todayFilteredProfit.toFixed(2)}
              </div>
              <div style={{ background: "linear-gradient(135deg, #10b981, #059669)", color: "white", padding: "8px 16px", borderRadius: "20px", fontWeight: "600", fontSize: "13px", boxShadow: "0 4px 6px -1px rgba(16, 185, 129, 0.2)" }}>
                Last 7 Days: ৳ {last7FilteredProfit.toFixed(2)}
              </div>
              <div style={{ background: "linear-gradient(135deg, #f59e0b, #f97316)", color: "white", padding: "8px 16px", borderRadius: "20px", fontWeight: "600", fontSize: "13px", boxShadow: "0 4px 6px -1px rgba(245, 158, 11, 0.15)" }}>
                Total (Selected): ৳ {totalFilteredProfit.toFixed(2)}
              </div>
            </div>
          </div>

          <div style={{ overflowX: "auto", margin: "0 -24px", padding: "0 24px" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px", textAlign: "left", minWidth: "650px" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #e2e8f0", color: "#64748b", textTransform: "uppercase" }}>
                  <th style={{ padding: "12px 8px" }}>Date</th>
                  <th style={{ padding: "12px 8px" }}>Type</th>
                  <th style={{ padding: "12px 8px" }}>Customer</th>
                  <th style={{ padding: "12px 8px" }}>RMB (¥)</th>
                  <th style={{ padding: "12px 8px" }}>BDT (৳)</th>
                  <th style={{ padding: "12px 8px" }}>Profit</th>
                  <th style={{ padding: "12px 8px", textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentTransactions.length === 0 ? (
                  <tr><td colSpan="7" style={{ padding: "24px", textAlign: "center", color: "#94a3b8" }}>No transactions found.</td></tr>
                ) : (
                  currentTransactions.map((txn, idx) => (
                    <tr key={idx} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ padding: "12px 8px" }}>{txn.date}</td>
                      <td style={{ padding: "12px 8px", fontWeight: "bold", color: txn.type === "Sell" ? "#ef4444" : "#22c55e" }}>{txn.type}</td>
                      <td style={{ padding: "12px 8px", fontWeight: "600", color: "#0f172a" }}>{txn.customerName}</td>
                      <td style={{ padding: "12px 8px" }}>¥{txn.rmb}</td>
                      <td style={{ padding: "12px 8px", color: "#475569" }}>৳{txn.bdt}</td>
                      <td style={{ padding: "12px 8px", fontWeight: "700", color: txn.profit > 0 ? "#16a34a" : (txn.profit < 0 ? "#ef4444" : "#94a3b8") }}>
                        {txn.profit !== 0 ? `${txn.profit > 0 ? '+৳' : '-৳'}${Math.abs(txn.profit).toFixed(2)}` : "—"}
                      </td>
                      <td style={{ padding: "12px 8px", textAlign: "right" }}>
                        <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                          <button onClick={() => handleViewVoucher(txn)} style={{ background: "#eef2ff", color: "#0B47B3", border: "1px solid #c7d2fe", padding: "6px", borderRadius: "6px", cursor: "pointer", display: "flex", alignItems: "center" }} title="View Voucher">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                          </button>
                          <button onClick={() => handleDeleteTxn(txn.id)} style={{ background: "#fee2e2", color: "#ef4444", border: "1px solid #fecaca", padding: "6px", borderRadius: "6px", cursor: "pointer", display: "flex", alignItems: "center" }} title="Delete Transaction">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "20px", borderTop: "1px solid #e2e8f0", paddingTop: "16px" }}>
              <span style={{ fontSize: "13px", color: "#64748b" }}>Showing page {currentPage} of {totalPages}</span>
              <div style={{ display: "flex", gap: "8px" }}>
                <button onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1} style={{ padding: "6px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", background: currentPage === 1 ? "#f8fafc" : "white", cursor: currentPage === 1 ? "not-allowed" : "pointer" }}>Prev</button>
                <button onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages} style={{ padding: "6px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", background: currentPage === totalPages ? "#f8fafc" : "white", cursor: currentPage === totalPages ? "not-allowed" : "pointer" }}>Next</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* RECEIPT MODAL POPUP */}
      {receiptModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.6)", backdropFilter: "blur(4px)", display: "flex", alignItems: "flex-start", justifyContent: "center", zIndex: 999, padding: "max(12px, env(safe-area-inset-top)) 12px 24px", overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
          <div style={{ background: "white", borderRadius: 18, width: "100%", maxWidth: 500, overflow: "hidden", boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)", margin: "0 auto" }}>
            <div id="receipt-print-area" style={{ display: "flex", justifyContent: "center", background: "#edf5ff", padding: "clamp(8px, 3vw, 16px)" }}>
              <ReceiptMobile {...buildReceiptProps(receiptModal)} />
            </div>
            <div style={{ padding: "12px clamp(12px, 5vw, 24px)", background: "#f8fafc", borderTop: "1px solid #e2e8f0", display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button onClick={() => setReceiptModal(null)} style={{ flex: "1 1 100px", padding: "10px", background: "white", border: "1px solid #cbd5e1", borderRadius: "8px", fontWeight: "600", color: "#475569", cursor: "pointer" }}>Close</button>
              <button onClick={handleDownloadReceipt} disabled={isReceiptExporting} style={{ flex: "1 1 100px", padding: "10px", background: "white", border: "1px solid #b9d1f7", borderRadius: "8px", fontWeight: "600", color: "#0B47B3", cursor: isReceiptExporting ? "wait" : "pointer", opacity: isReceiptExporting ? 0.7 : 1 }} title="Download JPG">
                <span aria-hidden>⇩</span> JPG
              </button>
              <button onClick={handleShareReceipt} disabled={isReceiptExporting} style={{ flex: "1 1 100px", padding: "10px", background: "#0B47B3", border: "none", borderRadius: "8px", fontWeight: "600", color: "white", cursor: isReceiptExporting ? "wait" : "pointer", opacity: isReceiptExporting ? 0.7 : 1 }} title="Share receipt image">
                <span aria-hidden>↗</span> {isReceiptExporting ? "Preparing..." : "Share"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
